import  { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useInView } from 'react-intersection-observer';

// Cohesive Color Palette
const COLORS = {
  primary: '#2563eb',      // Elegant blue
  secondary: '#7c3aed',    // Soft purple
  accent: '#f59e0b',       // Warm amber
  dark: '#1e293b',         // Deep blue
  light: '#f8fafc',        // Light background
  white: '#ffffff',         // Pure white
  gray: '#64748b',         // Muted gray
  lightGray: '#f1f5f9',    // Very light gray
  gradient: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #2563eb 100%)',
  textGradient: 'linear-gradient(135deg, #1e293b 0%, #2563eb 50%, #7c3aed 100%)'
};

// Premium Styled Components
const BookingSection = styled.section`
  position: relative;
  padding: 6rem 0;
  background: linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.light} 50%, ${COLORS.lightGray} 100%);
  overflow: hidden;
`;

const BookingContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
`;

const WizardContainer = styled(motion.div)`
  background: linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.light} 100%);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 
    0 25px 50px rgba(37, 99, 235, 0.1),
    0 10px 20px rgba(37, 99, 235, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(37, 99, 235, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${COLORS.gradient};
    border-radius: 24px 24px 0 0;
  }
`;

const WizardHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const WizardTitle = styled(motion.h3)`
  font-size: 2rem;
  font-weight: 700;
  background: ${COLORS.textGradient};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
`;

const WizardSubtitle = styled(motion.p)`
  color: ${COLORS.gray};
  font-size: 1rem;
  line-height: 1.6;
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 0.5rem;
`;

const ProgressStep = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  
  .step-number {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    background: ${props => props.active ? COLORS.gradient : props.completed ? COLORS.primary : 'rgba(37, 99, 235, 0.1)'};
    color: ${props => props.active || props.completed ? COLORS.white : COLORS.gray};
    border: 2px solid ${props => props.active || props.completed ? COLORS.primary : 'rgba(37, 99, 235, 0.2)'};
    transition: all 0.3s ease;
  }
  
  .step-label {
    font-size: 0.75rem;
    color: ${props => props.active ? COLORS.primary : COLORS.gray};
    font-weight: ${props => props.active ? '600' : '400'};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const StepContent = styled(motion.div)`
  min-height: 200px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: ${COLORS.dark};
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid rgba(37, 99, 235, 0.2);
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  
  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
    transform: translateY(-2px);
  }
  
  &::placeholder {
    color: ${COLORS.gray};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid rgba(37, 99, 235, 0.2);
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
    transform: translateY(-2px);
  }
`;

const DatePickerWrapper = styled.div`
  .react-datepicker {
    width: 100%;
    border: 2px solid rgba(37, 99, 235, 0.2);
    border-radius: 12px;
    box-shadow: 0 10px 25px rgba(37, 99, 235, 0.1);
  }
  
  .react-datepicker__input-container input {
    width: 100%;
    padding: 1rem 1.25rem;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(5px);
    cursor: pointer;
    
    &:focus {
      outline: none;
      box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem 1.25rem;
  border: 2px solid rgba(37, 99, 235, 0.2);
  border-radius: 12px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  
  &:focus {
    outline: none;
    border-color: ${COLORS.primary};
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
    transform: translateY(-2px);
  }
`;

const WizardButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1rem;
`;

const Button = styled(motion.button)`
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  font-size: 0.95rem;
  
  ${props => props.primary ? `
    background: ${COLORS.gradient};
    color: ${COLORS.white};
    box-shadow: 0 10px 25px rgba(37, 99, 235, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 15px 35px rgba(37, 99, 235, 0.4);
    }
  ` : `
    background: rgba(37, 99, 235, 0.1);
    color: ${COLORS.primary};
    border-color: ${COLORS.primary};
    
    &:hover {
      background: rgba(37, 99, 235, 0.2);
      transform: translateY(-2px);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%);
  color: ${COLORS.white};
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
  box-shadow: 0 10px 25px rgba(37, 99, 235, 0.3);
`;

const ErrorMessage = styled(motion.div)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: ${COLORS.white};
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
  box-shadow: 0 10px 25px rgba(239, 68, 68, 0.3);
  margin-bottom: 1rem;
`;

const BookingFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(37, 99, 235, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(37, 99, 235, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(37, 99, 235, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(37, 99, 235, 0.2);
  }
`;

const FeatureIcon = styled.span`
  font-size: 1.25rem;
`;

const FeatureText = styled.span`
  font-size: 0.875rem;
  color: ${COLORS.gray};
  font-weight: 500;
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: ${props => props.size || '60px'};
  height: ${props => props.size || '60px'};
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(37, 99, 235, 0.15));
  border: 1px solid rgba(124, 58, 237, 0.2);
  border-radius: 50%;
  backdrop-filter: blur(10px);
`;

const Booking = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [rooms, setRooms] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    checkIn: null,
    checkOut: null,
    roomType: '',
    guests: 1,
    message: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Fetch rooms from database
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const roomsCollection = collection(db, 'rooms');
        const roomsSnapshot = await getDocs(roomsCollection);
        const roomsData = roomsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRooms(roomsData);
        
        // Set default room type to first available room
        if (roomsData.length > 0) {
          setFormData(prev => ({ ...prev, roomType: roomsData[0].id }));
        }
      } catch (error) {
        console.error('Error fetching rooms:', error);
      }
    };

    fetchRooms();
  }, []);

  const steps = [
    { id: 1, label: 'Personal Info', required: ['name', 'email', 'phone'] },
    { id: 2, label: 'Dates', required: ['checkIn', 'checkOut', 'guests'] },
    { id: 3, label: 'Room', required: ['roomType'] },
    { id: 4, label: 'Details', required: [] }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateCurrentStep = () => {
    const currentStepData = steps.find(step => step.id === currentStep);
    if (!currentStepData) return false;
    
    return currentStepData.required.every(field => {
      const value = formData[field];
      
      // Handle different data types
      if (field === 'checkIn' || field === 'checkOut') {
        return value !== null;
      }
      
      // Handle string values
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      
      // Handle numbers
      if (typeof value === 'number') {
        return value > 0;
      }
      
      // Handle other types (null, undefined, boolean)
      return Boolean(value);
    });
  };

  const nextStep = () => {
    if (!validateCurrentStep()) {
      setError('Please fill in all required fields before proceeding.');
      return;
    }
    
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setError('');
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only allow submission on final step
    if (currentStep !== steps.length) {
      return;
    }
    
    // Validate before submitting
    if (!validateCurrentStep()) {
      setError('Please fill in all required fields before proceeding.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Add booking to Firestore
      const bookingData = {
        ...formData,
        checkInDate: formData.checkIn.toISOString(),
        checkOutDate: formData.checkOut.toISOString(),
        createdAt: new Date().toISOString(),
        status: 'pending'
      };

      await addDoc(collection(db, 'bookings'), bookingData);
      
      setSuccess(true);
      // Reset form to first available room
      setFormData({
        name: '',
        email: '',
        phone: '',
        checkIn: null,
        checkOut: null,
        roomType: rooms.length > 0 ? rooms[0].id : '',
        guests: 1,
        message: ''
      });
    } catch (err) {
      setError('Failed to submit booking. Please try again.');
      console.error('Booking error:', err);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: '✨', text: 'Instant Confirmation' },
    // { icon: '🔒', text: 'Secure Payment' },
    { icon: '📞', text: '24/7 Support' },
    { icon: '🎁', text: 'Exclusive Offers' }
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <h4 style={{ color: COLORS.dark, marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              Personal Information
            </h4>
            <FormRow>
              <FormGroup>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 98765 43210"
                  required
                />
              </FormGroup>
            </FormRow>
          </StepContent>
        );
      
      case 2:
        return (
          <StepContent
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <h4 style={{ color: COLORS.dark, marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              Select Your Dates
            </h4>
            <FormRow>
              <FormGroup>
                <Label htmlFor="checkIn">Check-in Date</Label>
                <DatePickerWrapper>
                  <DatePicker
                    selected={formData.checkIn}
                    onChange={(date) => setFormData(prev => ({ ...prev, checkIn: date }))}
                    selectsStart
                    startDate={formData.checkIn}
                    endDate={formData.checkOut}
                    minDate={new Date()}
                    placeholderText="Select check-in date"
                    required
                    dateFormat="MMMM d, yyyy"
                  />
                </DatePickerWrapper>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="checkOut">Check-out Date</Label>
                <DatePickerWrapper>
                  <DatePicker
                    selected={formData.checkOut}
                    onChange={(date) => setFormData(prev => ({ ...prev, checkOut: date }))}
                    selectsEnd
                    startDate={formData.checkIn}
                    endDate={formData.checkOut}
                    minDate={formData.checkIn || new Date()}
                    placeholderText="Select check-out date"
                    required
                    dateFormat="MMMM d, yyyy"
                  />
                </DatePickerWrapper>
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label htmlFor="guests">Number of Guests</Label>
                <Select
                  id="guests"
                  name="guests"
                  value={formData.guests}
                  onChange={handleChange}
                >
                  <option value={1}>1 Guest</option>
                  <option value={2}>2 Guests</option>
                  <option value={3}>3 Guests</option>
                  <option value={4}>4 Guests</option>
                  <option value={5}>5+ Guests</option>
                </Select>
              </FormGroup>
            </FormRow>
          </StepContent>
        );
      
      case 3:
        return (
          <StepContent
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <h4 style={{ color: COLORS.dark, marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              Choose Your Room
            </h4>
            <FormRow>
              <FormGroup>
                <Label htmlFor="roomType">Room Type</Label>
                <Select
                  id="roomType"
                  name="roomType"
                  value={formData.roomType}
                  onChange={handleChange}
                  disabled={rooms.length === 0}
                >
                  {rooms.length === 0 ? (
                    <option value="">Loading rooms...</option>
                  ) : (
                    rooms.map(room => (
                      <option key={room.id} value={room.id}>
                        {room.name || room.type || room.id} - ₹{room.price || '0'}/night
                      </option>
                    ))
                  )}
                </Select>
              </FormGroup>
            </FormRow>
          </StepContent>
        );
      
      case 4:
        return (
          <StepContent
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <h4 style={{ color: COLORS.dark, marginBottom: '1.5rem', fontSize: '1.5rem' }}>
              Special Requests
            </h4>
            <FormRow>
              <FormGroup>
                <Label htmlFor="message">Any special requirements or requests?</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Let us know if you have any special requests..."
                />
              </FormGroup>
            </FormRow>
          </StepContent>
        );
      
      default:
        return null;
    }
  };

  if (success) {
    return (
      <BookingSection>
        <BookingContainer>
          <SuccessMessage
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            🎉 Booking Request Submitted Successfully!
            <br />
            We'll contact you within 24 hours to confirm your reservation.
          </SuccessMessage>
        </BookingContainer>
      </BookingSection>
    );
  }

  return (
    <BookingSection>
      <FloatingElements>
        <FloatingElement
          style={{ top: '10%', right: '5%' }}
          animate={{
            y: [0, -15, 0],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <FloatingElement
          size="40px"
          style={{ bottom: '20%', left: '8%' }}
          animate={{
            y: [0, -10, 0],
            rotate: [0, -180, -360]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
      </FloatingElements>
      
      <BookingContainer>
        <WizardContainer
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <WizardHeader>
            <WizardTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Reserve Your Paradise
            </WizardTitle>
            <WizardSubtitle
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Complete your booking in just 4 simple steps
            </WizardSubtitle>
          </WizardHeader>

          <ProgressBar>
            {steps.map((step, index) => (
              <ProgressStep
                key={step.id}
                active={currentStep === step.id}
                completed={currentStep > step.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="step-number">{step.id}</div>
                <div className="step-label">{step.label}</div>
              </ProgressStep>
            ))}
          </ProgressBar>

          <BookingFeatures>
            {features.map((feature, index) => (
              <FeatureItem
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureText>{feature.text}</FeatureText>
              </FeatureItem>
            ))}
          </BookingFeatures>

          <AnimatePresence mode="wait">
            {error && (
              <ErrorMessage
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                {error}
              </ErrorMessage>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} noValidate>
            <AnimatePresence mode="wait">
              {renderStep()}
            </AnimatePresence>
          </form>

          <WizardButtons>
            <Button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              whileHover={{ scale: currentStep === 1 ? 1 : 1.02 }}
              whileTap={{ scale: currentStep === 1 ? 1 : 0.98 }}
            >
              Previous
            </Button>
            
            {currentStep === steps.length ? (
              <Button
                type="submit"
                onClick={handleSubmit}
                primary
                disabled={loading || !validateCurrentStep()}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                {loading ? 'Processing...' : 'Complete Booking'}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  nextStep();
                }}
                primary
                disabled={!validateCurrentStep()}
                whileHover={{ scale: !validateCurrentStep() ? 1 : 1.02 }}
                whileTap={{ scale: !validateCurrentStep() ? 1 : 0.98 }}
              >
                Next
              </Button>
            )}
          </WizardButtons>
        </WizardContainer>
      </BookingContainer>
    </BookingSection>
  );
};

export default Booking;
